import { useMemo } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { type StripeElementsOptions } from '@stripe/stripe-js';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { type StripePaymentProviderProps } from '../../types';
import {
  getMinimumAmount,
  getStripeKeyPrefix,
  loadStripeWithRetry,
} from '../../utils';

export const StripePaymentProvider = (props: StripePaymentProviderProps) => {
  const {
    children,
    amount,
    sessionId,
    publicKey,
    disablePolling = false,
    purchaseAttempt,
  } = props;

  const isOrderPaymentStatePollingEnabled = useFeatureFlag('CELS-4828-order-payment-state-polling-enabled'); // prettier-ignore
  const stripePaymentMethodConfig = useFeatureFlag('permanent-stripe-payment-method-config'); // prettier-ignore

  const defaultId = stripePaymentMethodConfig?.defaultId;
  const pollingDisabledId = stripePaymentMethodConfig?.pollingDisabledId;
  const paymentMethodConfiguration = isOrderPaymentStatePollingEnabled && !disablePolling ? defaultId : pollingDisabledId; // prettier-ignore

  const stripePromise = useMemo(
    () => loadStripeWithRetry(publicKey),
    [publicKey],
  );

  const options = useMemo<StripeElementsOptions>(
    () => ({
      amount: getMinimumAmount(amount),
      currency,
      mode,
      paymentMethodCreation,
      captureMethod,
      customerSessionClientSecret: sessionId,
      paymentMethodConfiguration,
    }),
    [amount, sessionId, paymentMethodConfiguration],
  );

  return (
    <Elements
      key={getStripeKeyPrefix(purchaseAttempt)}
      stripe={stripePromise}
      options={options}
    >
      {children}
    </Elements>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const currency = 'usd';
const mode = 'payment';
const paymentMethodCreation = 'manual';
const captureMethod = 'manual';
